import { createFileRoute, useNavigate } from "@tanstack/react-router";
import { useMemo, useRef, useState } from "react";
import { z } from "zod";
import { Mic, Send, Paperclip, X, ChevronUp, ChevronDown, Loader2 } from "lucide-react";
import { cn } from "@/lib/utils";
import { toast } from "sonner";
import { useServerFn } from "@tanstack/react-start";
import { sendChatMessage } from "@/lib/chat.functions";
import { useIsMobile } from "@/hooks/use-mobile";
import { MobileHomePage } from "@/components/app/mobile/mobile-home";
import { ConversationView } from "@/components/app/conversation-view";
import { useDictation } from "@/hooks/use-dictation";
import { usePlan } from "@/hooks/use-plan";
import { modelLabelForPlan } from "@/lib/ai/_ai-model";
import { openWhatsAppContact } from "@/lib/whatsapp-contact";

const HomeSearchSchema = z.object({ c: z.string().uuid().optional() });

export const Route = createFileRoute("/_authenticated/app/")({
  validateSearch: (s) => HomeSearchSchema.parse(s),
  component: HomePage,
});

const AREAS = [
  "Privacy", "Bancario e Assicurativo", "Lavoro", "Civile", "Societario",
  "Famiglia", "Tributario", "Proprietà Intellettuale", "Amministrativo",
  "Penale", "Diritto dei Consumatori", "Real Estate", "Crisi d'Impresa",
  "Compliance", "Diritto Scolastico",
] as const;

const MODES = [
  { id: "ask", label: "Chiedi a LexAI" },
  { id: "draft", label: "Redazione Atto" },
  { id: "compare", label: "Conformi e difformi" },
] as const;

type Mode = typeof MODES[number]["id"];

function HomePage() {
  const isMobile = useIsMobile();
  const search = Route.useSearch();
  if (search.c) return <ConversationView id={search.c} />;
  return isMobile ? <MobileHomePage /> : <DesktopHomePage />;
}

function DesktopHomePage() {
  const navigate = useNavigate();
  const [area, setArea] = useState<string | null>(null);
  const [mode, setMode] = useState<Mode>("ask");
  const [engine, setEngineState] = useState<"auto" | "claude">(() => {
    if (typeof window === "undefined") return "auto";
    return ((localStorage.getItem("lex.engine") as "auto" | "claude") || "auto");
  });
  const setEngine = (v: "auto" | "claude") => {
    setEngineState(v);
    if (typeof window !== "undefined") localStorage.setItem("lex.engine", v);
  };
  const [input, setInput] = useState("");
  const [busy, setBusy] = useState(false);
  const [filesOpen, setFilesOpen] = useState(false);
  const [files, setFiles] = useState<File[]>([]);
  const fileRef = useRef<HTMLInputElement>(null);
  const sendChat = useServerFn(sendChatMessage);
  const { listening, start: startDictation } = useDictation({
    onText: (t) => setInput((v) => (v ? v + " " + t : t).trim()),
  });
  const { plan } = usePlan();
  const isStudio = plan === "studio";

  const canSubmit = useMemo(() => input.trim().length > 0, [input]);


  const submit = async () => {
    if (!canSubmit || busy) return;
    const prefix = area ? `[Ambito: ${area}]\n` : "";
    const msg = prefix + input.trim();

    if (mode === "draft") {
      navigate({ to: "/app/drafting", search: { prompt: msg } as any });
      return;
    }
    if (mode === "compare") {
      navigate({ to: "/app/research", search: { q: msg, mode: "compare" } as any });
      return;
    }
    setBusy(true);
    try {
      const effectiveEngine = isStudio ? engine : "auto";
      const res = await sendChat({ data: { conversationId: null, message: msg, engine: effectiveEngine } });
      if (!res?.ok) {
        const err: any = res;
        if (err?.upgradeRequired) {
          toast.error(err.error || "Funzione disponibile solo in Studio", {
            action: { label: "Passa a Studio", onClick: () => openWhatsAppContact("Ciao Gianluca, vorrei passare al piano Studio di LexAI Pro.") },
          });
        } else {
          toast.error(err?.error || "Errore");
        }
      } else {
        toast.success("Conversazione salvata");
        navigate({ to: "/app", search: { c: res.conversationId } as any });
      }
    } catch (e: any) {
      toast.error(e?.message || "Errore di rete");
    } finally {
      setBusy(false);
    }
  };

  return (
    <div className="relative flex h-full min-h-0 flex-col bg-background">
      <div className="flex flex-1 items-center justify-center px-4 py-6">
        <div className="w-full max-w-3xl">
          <h1 className="text-center text-3xl font-semibold text-foreground sm:text-5xl">
            Come posso aiutarti?
          </h1>
          <p className="mt-3 text-center text-base text-muted-foreground">
            Scegli un'area, una modalità e scrivi la tua domanda.
          </p>

          {/* Aree */}
          <div className="mt-8 flex flex-wrap justify-center gap-2">
            {AREAS.map((a) => {
              const isBeta = a === "Diritto Scolastico";
              const active = area === a;
              return (
                <button
                  key={a}
                  onClick={() => setArea(active ? null : a)}
                  className={cn(
                    "min-h-[44px] inline-flex items-center gap-2 rounded-full border-2 px-4 py-2 text-[15px] font-medium transition-colors",
                    active
                      ? "border-primary bg-primary text-primary-foreground"
                      : "border-border-strong bg-background text-foreground hover:bg-secondary",
                  )}
                >
                  {a}
                  {isBeta && (
                    <span className={cn(
                      "rounded px-1.5 py-0.5 text-[10px] font-bold",
                      active ? "bg-secondary text-foreground" : "bg-primary text-primary-foreground",
                    )}>BETA</span>
                  )}
                </button>
              );
            })}
          </div>

          {/* Composer */}
          <div className="mt-8 rounded-2xl border-2 border-border-strong bg-background p-4 shadow-md focus-within:border-primary focus-within:ring-2 focus-within:ring-primary/30">
            <textarea
              value={input}
              onChange={(e) => setInput(e.target.value)}
              placeholder="Scrivi qui la tua domanda…"
              rows={3}
              disabled={busy}
              aria-label="Quesito"
              className="w-full resize-none border-0 bg-transparent px-1 py-1 text-[18px] leading-relaxed text-foreground outline-none placeholder:text-muted-foreground disabled:opacity-60"
              onKeyDown={(e) => { if (e.key === "Enter" && (e.metaKey || e.ctrlKey)) submit(); }}
            />

            {/* Modalità */}
            <div className="mt-4 flex flex-wrap items-center gap-2">
              {MODES.map((m) => {
                const active = mode === m.id;
                return (
                  <button
                    key={m.id}
                    onClick={() => setMode(m.id)}
                    className={cn(
                      "min-h-[44px] rounded-full border-2 px-4 py-2 text-[15px] font-semibold transition-colors",
                      active
                        ? "border-primary bg-primary text-primary-foreground"
                        : "border-border-strong bg-background text-foreground hover:bg-secondary",
                    )}
                  >
                    {m.label}
                  </button>
                );
              })}
              {/* Selettore motore AI — solo Studio */}
              {isStudio ? (
                <div className="ml-auto inline-flex items-center gap-1 rounded-full border-2 border-border-strong bg-background p-1" role="radiogroup" aria-label="Motore AI">
                  <button
                    type="button"
                    role="radio"
                    aria-checked={engine === "auto"}
                    onClick={() => setEngine("auto")}
                    title="GPT-5.2 (default)"
                    className={cn(
                      "min-h-[36px] rounded-full px-3 py-1 text-[13px] font-semibold transition-colors",
                      engine === "auto" ? "bg-primary text-primary-foreground" : "text-foreground hover:bg-secondary",
                    )}
                  >
                    GPT-5.2
                  </button>
                  <button
                    type="button"
                    role="radio"
                    aria-checked={engine === "claude"}
                    onClick={() => setEngine("claude")}
                    title="Claude Sonnet 4.5 — rigore forense rinforzato"
                    className={cn(
                      "min-h-[36px] rounded-full px-3 py-1 text-[13px] font-semibold transition-colors",
                      engine === "claude" ? "bg-primary text-primary-foreground" : "text-foreground hover:bg-secondary",
                    )}
                  >
                    Claude
                  </button>
                </div>
              ) : (
                <div className="ml-auto inline-flex items-center gap-2 rounded-full border border-border bg-secondary px-3 py-1 text-[12px] text-muted-foreground" title="Modello assegnato dal piano">
                  <span>Modello: <strong className="text-foreground">{modelLabelForPlan(plan)}</strong></span>
                </div>
              )}
            </div>

            {/* Azioni */}
            <div className="mt-4 flex items-center justify-between gap-3 border-t border-border pt-3">
              <button
                onClick={startDictation}
                aria-label="Detta con la voce"
                className={cn(
                  "min-h-[56px] inline-flex items-center gap-2 rounded-full border-2 px-5 py-3 text-[15px] font-semibold transition-colors",
                  listening
                    ? "border-destructive bg-destructive/10 text-destructive"
                    : "border-border-strong bg-background text-foreground hover:bg-secondary",
                )}
              >
                <Mic className="h-5 w-5" />
                <span>{listening ? "In ascolto…" : "Detta"}</span>
              </button>

              <button
                onClick={submit}
                disabled={!canSubmit || busy}
                aria-label="Invia"
                className={cn(
                  "min-h-[56px] inline-flex items-center gap-2 rounded-full px-6 py-3 text-[16px] font-bold transition-colors",
                  canSubmit && !busy
                    ? "bg-primary text-primary-foreground hover:bg-[hsl(var(--primary-deep))]"
                    : "bg-muted text-muted-foreground cursor-not-allowed",
                )}
              >
                {busy ? <Loader2 className="h-5 w-5 animate-spin" /> : <Send className="h-5 w-5" />}
                <span>{busy ? "Invio in corso…" : "Invia"}</span>
              </button>
            </div>

            <div className="mt-3 text-[13px] text-muted-foreground">
              Suggerimento: premi <kbd className="rounded border border-border-strong bg-secondary px-1.5 py-0.5 text-[12px] font-medium text-foreground">⌘/Ctrl</kbd> + <kbd className="rounded border border-border-strong bg-secondary px-1.5 py-0.5 text-[12px] font-medium text-foreground">Invio</kbd> per inviare.
            </div>

            {busy && (
              <div className="mt-4 flex items-center gap-3 rounded-xl border-2 border-primary bg-[hsl(var(--primary-muted))] px-4 py-3 text-[16px] font-medium text-[hsl(var(--primary-deep))]">
                <Loader2 className="h-5 w-5 animate-spin" />
                <span>Sto pensando<span className="inline-block animate-pulse">…</span></span>
              </div>
            )}
          </div>

          {/* Allegati */}
          <div className="mt-4 rounded-2xl border-2 border-dashed border-border-strong bg-background">
            <button
              onClick={() => setFilesOpen((v) => !v)}
              className="flex w-full min-h-[56px] items-center gap-3 px-4 py-3 text-left text-[16px] font-semibold text-foreground hover:bg-secondary"
            >
              <Paperclip className="h-5 w-5 text-primary" />
              <span>Aggiungi un documento</span>
              {files.length > 0 && (
                <span className="rounded-full bg-primary px-2.5 py-0.5 text-[13px] font-bold text-primary-foreground">
                  {files.length}
                </span>
              )}
              {filesOpen
                ? <ChevronUp className="ml-auto h-5 w-5 text-muted-foreground" />
                : <ChevronDown className="ml-auto h-5 w-5 text-muted-foreground" />}
            </button>
            {filesOpen && (
              <div className="border-t border-border px-4 py-4">
                <input
                  ref={fileRef}
                  type="file"
                  multiple
                  accept=".pdf,.docx,.txt"
                  className="hidden"
                  onChange={(e) => {
                    const list = Array.from(e.target.files ?? []);
                    setFiles((f) => [...f, ...list]);
                    if (fileRef.current) fileRef.current.value = "";
                  }}
                />
                <button
                  onClick={() => fileRef.current?.click()}
                  className="min-h-[48px] rounded-md border-2 border-dashed border-primary bg-[hsl(var(--primary-muted))] px-4 py-2 text-[15px] font-semibold text-[hsl(var(--primary-deep))] hover:bg-[hsl(var(--primary-muted))]/70"
                >
                  Carica PDF, DOCX o TXT
                </button>
                {files.length > 0 && (
                  <ul className="mt-3 space-y-2">
                    {files.map((f, i) => (
                      <li key={i} className="flex items-center gap-3 rounded-lg border border-border-strong bg-secondary px-3 py-2 text-[14px] text-foreground">
                        <Paperclip className="h-4 w-4 text-muted-foreground" />
                        <span className="flex-1 truncate">{f.name}</span>
                        <button
                          onClick={() => setFiles((arr) => arr.filter((_, j) => j !== i))}
                          aria-label={`Rimuovi ${f.name}`}
                          className="inline-flex h-9 w-9 items-center justify-center rounded-md text-destructive hover:bg-destructive/10"
                        >
                          <X className="h-4 w-4" />
                        </button>
                      </li>
                    ))}
                  </ul>
                )}
              </div>
            )}
          </div>

          <div className="mt-6 text-center text-[13px] text-muted-foreground">
            Risposte basate sui tuoi documenti, sempre aggiornate. {" "}
            <a href="mailto:beleadcreativestudio@proton.me" className="font-semibold text-primary underline-offset-4 hover:underline">
              Hai bisogno di aiuto? Contattaci →
            </a>
          </div>
        </div>
      </div>
    </div>
  );
}
